import type { NoteSchema, Quote } from "@stryker/core/types/quotes";
import { useQuery } from "@tanstack/react-query";
import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import DeleteIcon from "@/assets/icons/trash.svg?react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { TextInput } from "@/components/ui/textInput";
import { additionalNotesQueryOptions } from "@/hooks/useAdditionalNotes";

type AdditionalNotesProps = {
  additional_notes: string[];
  notes: NoteSchema[];
  setAdditional_notes: Dispatch<SetStateAction<string[]>>;
  updateNote: (index: number, value: NoteSchema) => void;
  removeNote: (index: number) => void;
  addNote: () => void;
  quote: Quote | undefined;
};
export default function AdditionalNotes({
  notes,
  quote,
  additional_notes,
  setAdditional_notes,
  updateNote,
  removeNote,
  addNote,
}: AdditionalNotesProps) {
  const { data: notesOptions = [] } = useQuery({
    ...additionalNotesQueryOptions("composite"),
    staleTime: 1000000,
    select: (response) => {
      return response.quote_additional_notes;
    },
  });

  const mappedNotesOptions = notesOptions.map((note) => {
    return { label: note.name, value: String(note.id) };
  });

  return (
    <Card className="p-6 md:p-10">
      <CardTitle>Additional Notes</CardTitle>
      <CardContent className="p-0 pt-6 md:pt-10">
        <div className="space-y-4">
          <Label>Additional notes</Label>
          <MultiSelect
            value={additional_notes}
            defaultValue={quote?.additional_notes.map((id) => String(id)) ?? []}
            options={mappedNotesOptions}
            onValueChange={(selected) => {
              setAdditional_notes(selected);
            }}
          />

          <div className="space-y-3">
            {notes.map((note, index) => (
              <CustomNote
                key={note.id ?? index}
                index={index}
                note={note}
                updateNote={updateNote}
                removeNote={removeNote}
              />
            ))}
          </div>

          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={addNote}>
              Add Note
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type CustomNoteProps = {
  index: number;
  note: NoteSchema;
  updateNote: (index: number, value: NoteSchema) => void;
  removeNote: (index: number) => void;
};

function CustomNote({ index, note, removeNote, updateNote }: CustomNoteProps) {
  const handleChange = (key: keyof NoteSchema, value: string) => {
    const updatedNote = { ...note, [key]: value };
    updateNote(index, updatedNote);
  };

  const handleDelete = () => {
    removeNote(index);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-gray-700">Note {index + 1}</span>
        <Button
          variant="outline"
          size="icon"
          type="button"
          onClick={handleDelete}
          aria-label="Delete note"
          title="Delete note"
        >
          <DeleteIcon className="h-4 w-4 text-red-500" />
        </Button>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 md:items-end">
        <TextInput
          label="Note"
          value={note.note_name ?? ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange("note_name", e.target.value)}
        />
        <TextInput
          label="Name of note"
          value={note.custom_note ?? ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange("custom_note", e.target.value)}
        />
      </div>
    </div>
  );
}
